import React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';
import {
  CLIENT_JOURNEY_STEPS,
  getClientJourneyIndex,
  resolveClientJourneyIndex,
} from './clientDashboardJourney';

export type ClientDashboardJourneyBarProps = {
  state: number;
  capabilityCount?: number;
  variant?: 'horizontal' | 'vertical';
  className?: string;
};

export function ClientDashboardJourneyBar({
  state,
  capabilityCount = 0,
  variant = 'horizontal',
  className,
}: ClientDashboardJourneyBarProps) {
  const journeyIndex =
    variant === 'horizontal'
      ? resolveClientJourneyIndex(state, capabilityCount)
      : getClientJourneyIndex(state);

  if (variant === 'vertical') {
    return (
      <div className={cn('client-dash__journey', className)} aria-label="Assessment journey">
        <p className="client-dash__journey-label">Your journey</p>
        <ol className="client-dash__journey-steps">
          {CLIENT_JOURNEY_STEPS.map((step, index) => {
            const done = index < journeyIndex;
            const active = index === journeyIndex;
            return (
              <li
                key={step.key}
                className={cn(
                  'client-dash__journey-step',
                  done && 'is-done',
                  active && 'is-active'
                )}
              >
                <span className="client-dash__journey-marker" aria-hidden>
                  {done ? <Check size={12} strokeWidth={2.5} /> : index + 1}
                </span>
                <span className="client-dash__journey-text">
                  <span className="client-dash__journey-name">{step.label}</span>
                  <span className="client-dash__journey-hint">{step.hint}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    );
  }

  const stepCount = CLIENT_JOURNEY_STEPS.length;
  const trackProgress =
    stepCount <= 1 ? 0 : Math.min(1, Math.max(0, journeyIndex / (stepCount - 1)));

  return (
    <nav
      className={cn('client-dash__journey-bar', className)}
      aria-label="Assessment journey"
    >
      <p className="client-dash__journey-bar-label">Your journey</p>
      <div
        className="client-dash__journey-bar-layout"
        style={{ ['--journey-progress' as string]: String(trackProgress) }}
      >
        <div className="client-dash__journey-bar-track" aria-hidden>
          <span className="client-dash__journey-bar-track-bg" />
          <span className="client-dash__journey-bar-track-fill" />
        </div>
        <ol className="client-dash__journey-bar-steps">
          {CLIENT_JOURNEY_STEPS.map((step, index) => {
            const done = index < journeyIndex;
            const active = index === journeyIndex;
            const showCapCount = step.key === 'capabilities' && capabilityCount > 0;

          let hint = step.hint;
          if (showCapCount) {
            hint = `${capabilityCount} selected`;
          } else if (active) {
            hint = `${step.hint} · now`;
          }

            return (
              <li
                key={step.key}
                className={cn(
                  'client-dash__journey-bar-step',
                  done && 'is-done',
                  active && 'is-active'
                )}
              >
                <span className="client-dash__journey-bar-marker" aria-hidden>
                  {done ? <Check size={14} strokeWidth={2.5} /> : index + 1}
                </span>
                <span className="client-dash__journey-bar-copy">
                  <span className="client-dash__journey-bar-name">{step.label}</span>
                  <span className="client-dash__journey-bar-hint">{hint}</span>
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </nav>
  );
}

export default ClientDashboardJourneyBar;
